import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { sidebarCollapsedAtom } from '@/store/ui';

const NARROW_QUERY = '(min-width: 768px) and (max-width: 1023px)';

/**
 * Effective sidebar state for the app shell + floating components like ActionBar.
 *
 * - `collapsed` is true if the user manually collapsed the sidebar OR if the
 *   viewport is in the "narrow desktop" range (md..lg) — both render the icon-only sidebar.
 * - `setCollapsed` only updates the user preference; viewport coercion still wins.
 * - `sidebarWidthPx` is the rendered desktop sidebar width (collapsed vs expanded).
 *   On mobile (<md) the sidebar is hidden entirely; consumers (e.g. ActionBar)
 *   should treat that breakpoint with their own `max-md:` overrides.
 */
export function useSidebar() {
  const [userCollapsed, setUserCollapsed] = useAtom(sidebarCollapsedAtom);
  const [narrowDesktop, setNarrowDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(NARROW_QUERY);
    const apply = () => setNarrowDesktop(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const collapsed = userCollapsed || narrowDesktop;
  return {
    collapsed,
    forcedNarrow: narrowDesktop,
    setCollapsed: setUserCollapsed,
    sidebarWidthPx: collapsed ? 64 : 232,
  };
}
